import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export interface CostEstimate {
    totalCredits: number;
    totalCostCents: number;
    tierCode: string;
    totalPages: number;
    insurancePages: number;
    contractorPages: number;
    costPerCreditCents: number;
}

interface CostConfirmationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    estimate: CostEstimate | null;
    onConfirm: () => void;
    onBack?: () => void;
    isProcessing?: boolean;
    confirmLabel?: string;
}

/**
 * Format the tier code as a display label
 */
function formatTierLabel(tierCode: string): string {
    return `${tierCode.toLowerCase()} tier`;
}

export function CostConfirmationDialog({
    open,
    onOpenChange,
    estimate,
    onConfirm,
    onBack,
    isProcessing = false,
    confirmLabel = "Process Documents"
}: CostConfirmationDialogProps) {
    if (!estimate) return null;

    const costPerPage = estimate.totalPages > 0
        ? estimate.totalCostCents / estimate.totalPages / 100
        : 0;
    const totalCost = estimate.totalCostCents / 100;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Processing Cost Confirmation</DialogTitle>
                    <DialogDescription>
                        Review the processing cost before continuing with metadata extraction.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Total Pages */}
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Total Pages:</span>
                        <span>{estimate.totalPages}</span>
                    </div>

                    {/* Insurance & Contractor pages - subsection style */}
                    <div className="pl-4 space-y-1">
                        <div className="flex items-center justify-between text-xs text-muted-foreground/70">
                            <span>Insurance Estimate:</span>
                            <span>{estimate.insurancePages} {estimate.insurancePages === 1 ? 'page' : 'pages'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground/70">
                            <span>Contractor Estimate:</span>
                            <span>{estimate.contractorPages} {estimate.contractorPages === 1 ? 'page' : 'pages'}</span>
                        </div>
                    </div>

                    {/* Cost Per Page */}
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Cost per page:</span>
                        <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                                {formatTierLabel(estimate.tierCode)}
                            </Badge>
                            <span>{formatCurrency(costPerPage)}</span>
                        </div>
                    </div>

                    {/* Total Cost */}
                    <div className="flex items-center justify-between text-sm pt-2 border-t">
                        <span className="text-muted-foreground">Total Cost:</span>
                        <span>{formatCurrency(totalCost)}</span>
                    </div>
                </div>

                <DialogFooter className="flex space-x-2 sm:justify-end">
                    {onBack ? (
                        <Button variant="outline" onClick={onBack} disabled={isProcessing}>Back</Button>
                    ) : (
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>Cancel</Button>
                    )}
                    <Button onClick={onConfirm} disabled={isProcessing}>
                        {isProcessing ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            confirmLabel
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
